import { useCallback, useState } from 'react';
import { usePreisToolFixedPrices } from '@/api/preis-tool/preis-tool.hooks';
import type { PreisToolItem } from '@/api/preis-tool/preis-tool.types';
import { DsButton } from '@/modules/internal-v2/ds';
import { computeBedarf, fmtDate, fmtEUR } from '../helpers';
import { DispositionBlock } from './disposition-block.component';
import { FixedPriceDialog } from './fixed-price-dialog.component';
import { PriceHistoryDialog } from './price-history-dialog.component';
import { SupplierOffers } from './supplier-offers.component';

interface WorkspaceProps {
  article: PreisToolItem | undefined;
  basket: string[];
  setBasket: (ids: string[]) => void;
  hasSupplierDeal: boolean;
  /** Whether the user may maintain purchase-side data (`PREIS_TOOL_MANAGE`). */
  canManage: boolean;
}

export function Workspace({ article, basket, setBasket, hasSupplierDeal, canManage }: WorkspaceProps) {
  if (!article) {
    return (
      <div className="pt-workspace">
        <div className="ds-empty" style={{ flex: 1 }}>
          <div>
            <div className="big">Kein Artikel ausgewählt</div>
            <div>Wähle einen Artikel oben in der Trefferliste.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <WorkspaceContent
      article={article}
      basket={basket}
      setBasket={setBasket}
      hasSupplierDeal={hasSupplierDeal}
      canEdit={canManage}
    />
  );
}

function WorkspaceContent({
  article,
  basket,
  setBasket,
  hasSupplierDeal,
  canEdit,
}: {
  article: PreisToolItem;
  basket: string[];
  setBasket: (ids: string[]) => void;
  hasSupplierDeal: boolean;
  canEdit: boolean;
}) {
  const inBasket = basket.includes(article.id);
  const bedarf = computeBedarf(article);
  const { data: fixedPriceData } = usePreisToolFixedPrices(article.id);
  const [fixedPricesOpen, setFixedPricesOpen] = useState(false);
  // The fixed price whose stock the dialog opens at (a click on its Lager cell).
  const [fixedPriceStockId, setFixedPriceStockId] = useState<string | undefined>();
  const openFixedPrices = useCallback((fixedPriceId?: string) => {
    setFixedPriceStockId(fixedPriceId);
    setFixedPricesOpen(true);
  }, []);
  const [historyOpen, setHistoryOpen] = useState(false);

  // The list comes sorted: active fixed prices first, cheapest on top.
  const activeFixedPrices = (fixedPriceData?.fixedPrices ?? []).filter((f) => !f.expired);
  const expiredFixedPriceCount = (fixedPriceData?.fixedPrices.length ?? 0) - activeFixedPrices.length;
  const bestFixedPrice = activeFixedPrices[0];
  // The tile is narrow and cuts the line off: the end date goes first, the
  // supplier is also named in the top row of the offer table.
  const fixedPriceSub = bestFixedPrice
    ? [
        `bis ${fmtDate(bestFixedPrice.validTo)}`,
        bestFixedPrice.supplierName,
        `${activeFixedPrices.length} Vereinbarung${activeFixedPrices.length === 1 ? '' : 'en'}`,
      ].join(' · ')
    : 'kein Fixpreis hinterlegt';

  return (
    <div className="pt-workspace">
      <div className="pt-ws-header">
        <div className="pt-ws-breadcrumb">Preis-Tool / Detail / {article.manufacturerName}</div>
        <div className="pt-ws-title-row">
          <span className="pt-ws-id">{article.id}</span>
          <span className="pt-ws-name">{article.name}</span>
          <div style={{ flex: 1 }} />
          <DsButton onClick={() => setHistoryOpen(true)}>Preisverlauf</DsButton>
          <DsButton onClick={() => {
            if (inBasket) setBasket(basket.filter((x) => x !== article.id));
            else setBasket([...basket, article.id]);
          }}>
            {inBasket ? '− In Basket' : '+ Zu Basket'}
          </DsButton>
        </div>
        <div className="pt-ws-meta">
          <span><span className="k">Hersteller</span><span className="v">{article.manufacturerName}</span></span>
        </div>
        {hasSupplierDeal && (
          <div className="pt-ws-flags">
            <span className="ds-flag deal">Lief. u. MEK</span>
          </div>
        )}
      </div>

      <div className="pt-ws-body">
        <div className="pt-metric-strip">
          <div className="pt-metric highlight">
            <div className="label">MEK <span className="hint">Moving Avg</span></div>
            <div className="value">€ {fmtEUR(article.movingAveragePrice)}</div>
            <div className="sub">Referenz</div>
          </div>
          <div className="pt-metric">
            <div className="label">
              Fixpreis
              <button className="ds-btn ghost small" onClick={() => openFixedPrices()}>
                {canEdit ? 'Pflegen' : 'Ansehen'}
              </button>
            </div>
            <div className="value">{bestFixedPrice ? `€ ${fmtEUR(bestFixedPrice.netPrice)}` : '—'}</div>
            <div className="sub ellipsis" title={fixedPriceSub}>
              {fixedPriceSub}
            </div>
          </div>
          <div className="pt-metric">
            <div className="label">Lagerbestand</div>
            <div className="value">{article.quantityOnStock}</div>
            <div className="sub">Min {article.minInventory} · Soll {article.desiredInventory}</div>
          </div>
          <div className="pt-metric">
            <div className="label">Kundenaufträge</div>
            <div className="value">{article.quantityOrderedByCustomers}</div>
            <div className="sub">noch nicht ausgeliefert</div>
          </div>
          <div className="pt-metric">
            <div className="label">Lieferant offen</div>
            <div className="value">{article.quantityOrderedFromVendors}</div>
            <div className="sub">offene Einkaufsaufträge</div>
          </div>
          <div className="pt-metric">
            <div className="label">Bedarf</div>
            <div className="value" style={{ color: bedarf > 0 ? 'var(--warn)' : 'var(--ink)' }}>{bedarf}</div>
            <div className="sub">{bedarf > 0 ? 'Nachbestellung empfohlen' : 'gedeckt'}</div>
          </div>
          <div className="pt-metric">
            <div className="label">Demand-Score</div>
            <div className="value">—<span style={{ fontSize: 10, color: 'var(--ink-3)' }}>/100</span></div>
            <div className="pt-dbar"><div className="fill" style={{ width: '0%' }} /></div>
          </div>
        </div>
        <DispositionBlock itemId={article.id} itemName={article.name} canEdit={canEdit} />
        <div className="pt-ws-offers">
          <SupplierOffers
            itemId={article.id}
            itemName={article.name}
            mek={article.movingAveragePrice}
            canEdit={canEdit}
            expiredFixedPriceCount={expiredFixedPriceCount}
            onOpenFixedPrices={openFixedPrices}
          />
        </div>
      </div>
      {historyOpen && <PriceHistoryDialog article={article} onClose={() => setHistoryOpen(false)} />}
      {fixedPricesOpen && (
        <FixedPriceDialog
          article={article}
          canEdit={canEdit}
          initialStockOpenId={fixedPriceStockId}
          onClose={() => setFixedPricesOpen(false)}
        />
      )}
    </div>
  );
}
